import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyBranding } from '../hooks/useBranding';
import { dashboardService } from '../services/dashboard.service';
import KpiCard from '../components/dashboard/KpiCard';
import { JobsTrendChart, JobsByLocationChart } from '../components/dashboard/DashboardCharts';
import localeConfig from '../config/locale.config';

const money = (amount) => {
    const n = Number(amount || 0);
    return `${localeConfig.currency.symbol}${n.toLocaleString(localeConfig.intlLocale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}`;
};

const formatMinutes = (minutes) => {
    if (minutes == null || Number.isNaN(Number(minutes))) return '—';
    const m = Number(minutes);
    if (m < 60) return `${m.toFixed(1)} min`;
    return `${(m / 60).toFixed(1)} hr`;
};

const Dashboard = () => {
    const { user } = useAuth();
    const { name } = useCompanyBranding();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await dashboardService.getDashboard();
            setData(result);
        } catch (err) {
            console.error('Failed to load vendor dashboard:', err);
            setError(err?.message || 'Failed to load dashboard');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    if (loading) {
        return (
            <div className="page-container">
                <div className="content-container flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="page-container">
                <div className="content-container">
                    <div className="card text-center py-12">
                        <p className="text-gray-600 mb-4">{error || 'Unable to load dashboard'}</p>
                        <button
                            type="button"
                            onClick={fetchDashboard}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const jobs = data.jobs_mtd || {};
    const revenue = data.revenue || {};
    const sla = data.sla || {};
    const ranking = data.ranking || {};
    const vendorInfo = data.vendor_info || {};
    const stats = data.statistics || {};

    return (
        <div className="page-container">
            <div className="content-container space-y-4 md:space-y-6">
                {/* Header */}
                <div className="card">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="min-w-0">
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900 truncate">
                                {vendorInfo.name || name} Dashboard
                            </h1>
                            <p className="text-gray-600 mt-1 text-sm md:text-base">
                                Platform health for your vendor operations
                                {vendorInfo.code ? ` · ${vendorInfo.code}` : ''}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={fetchDashboard}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 w-full sm:w-auto shrink-0"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                    </div>
                </div>

                {vendorInfo.is_suspended ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                        <p className="font-semibold">Suspended from new dispatch</p>
                        <p className="mt-1">
                            {vendorInfo.suspended_reason ||
                                'Your account is below the SLA threshold. You will not receive new job alerts until operations reinstates you.'}
                        </p>
                    </div>
                ) : null}

                {/* Jobs KPIs */}
                <section>
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 px-1">
                        Jobs
                    </h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        <KpiCard
                            title="Completed (MTD)"
                            value={jobs.completed_mtd ?? 0}
                            subtitle={`${jobs.created_mtd ?? 0} created`}
                            icon="check"
                            color="green"
                        />
                        <KpiCard
                            title="Active now"
                            value={jobs.active_now ?? data.active_services?.length ?? 0}
                            subtitle={`${data.pending_alerts?.length ?? 0} pending alerts`}
                            icon="activity"
                            color="blue"
                        />
                        <KpiCard
                            title="Cancelled (MTD)"
                            value={jobs.cancelled_mtd ?? 0}
                            subtitle={`${jobs.completion_rate_mtd ?? 0}% completion rate`}
                            icon="cancel"
                            color="red"
                        />
                        <KpiCard
                            title="Avg completion"
                            value={formatMinutes(jobs.avg_completion_minutes_mtd)}
                            subtitle={`Response ${formatMinutes(jobs.avg_response_minutes_mtd)}`}
                            icon="clock"
                            color="amber"
                        />
                    </div>
                </section>

                {/* Jobs charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    <JobsTrendChart trends={data.jobs_daily_trends} />
                    <JobsByLocationChart rows={data.jobs_by_location} />
                </div>

                {/* Jobs by driver (table / cards) */}
                {(data.jobs_by_driver || []).length > 0 && (
                    <section className="bg-white rounded-lg border border-gray-200 p-4 md:p-5 shadow-sm">
                        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1">Jobs by driver</h3>
                        <p className="text-xs text-gray-500 mb-4">This month</p>
                        <div className="overflow-x-auto -mx-1">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="text-left text-gray-500 border-b">
                                        <th className="py-2 px-2 font-medium">Driver</th>
                                        <th className="py-2 px-2 font-medium text-right">Created</th>
                                        <th className="py-2 px-2 font-medium text-right">Completed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.jobs_by_driver.slice(0, 10).map((d) => (
                                        <tr key={d.driver_id} className="border-b border-gray-100">
                                            <td className="py-2.5 px-2 text-gray-900">{d.driver_name}</td>
                                            <td className="py-2.5 px-2 text-right text-gray-700">{d.created_mtd}</td>
                                            <td className="py-2.5 px-2 text-right font-medium text-gray-900">{d.completed_mtd}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {/* Revenue */}
                <section>
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 px-1">
                        Revenue
                    </h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        <KpiCard
                            title="Gross (MTD)"
                            value={money(revenue.gross_mtd)}
                            subtitle={`${revenue.invoice_count_mtd ?? 0} invoices`}
                            icon="dollar"
                            color="blue"
                        />
                        <KpiCard
                            title="Commission (MTD)"
                            value={money(revenue.commission_mtd)}
                            subtitle="Platform share"
                            icon="trend"
                            color="purple"
                        />
                        <KpiCard
                            title="Net (MTD)"
                            value={money(revenue.net_mtd)}
                            subtitle={`Today ${money(revenue.today_net)}`}
                            icon="check"
                            color="green"
                        />
                        <KpiCard
                            title="Pending payouts"
                            value={revenue.pending_invoices ?? 0}
                            subtitle="Awaiting approval"
                            icon="briefcase"
                            color="amber"
                        />
                    </div>
                    {revenue.by_status && Object.keys(revenue.by_status).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {Object.entries(revenue.by_status).map(([status, bucket]) => (
                                <span
                                    key={status}
                                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                                >
                                    {status}: {bucket.count} · {money(bucket.net)}
                                </span>
                            ))}
                        </div>
                    )}
                </section>

                {/* SLA */}
                <section>
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 px-1">
                        SLA breaches
                    </h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        <KpiCard
                            title="Total breaches (MTD)"
                            value={sla.total_breaches_mtd ?? 0}
                            subtitle={`Timeout ${sla.timeout_minutes ?? 15} min`}
                            icon="alert"
                            color="red"
                        />
                        <KpiCard
                            title="Expired alerts"
                            value={sla.expired_alerts ?? 0}
                            subtitle="No response in time"
                            icon="clock"
                            color="amber"
                        />
                        <KpiCard
                            title="Slow responses"
                            value={sla.slow_responses ?? 0}
                            subtitle="Over response SLA"
                            icon="activity"
                            color="purple"
                        />
                        <KpiCard
                            title="ETA misses"
                            value={sla.eta_misses ?? 0}
                            subtitle="Actual > estimated"
                            icon="trend"
                            color="cyan"
                        />
                    </div>
                </section>

                {/* Ranking */}
                <section className="bg-white rounded-lg border border-gray-200 p-4 md:p-5 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <div>
                            <h2 className="text-base md:text-lg font-semibold text-gray-900">Vendor ranking</h2>
                            <p className="text-xs text-gray-500">Performance score this month</p>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                            <div>
                                <span className="text-gray-500">Your rank</span>
                                <p className="text-xl font-bold text-gray-900">
                                    {ranking.rank != null ? `#${ranking.rank}` : '—'}
                                    <span className="text-sm font-normal text-gray-500">
                                        {' '}/ {ranking.total_vendors ?? 0}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <span className="text-gray-500">Score</span>
                                <p className="text-xl font-bold text-blue-600">{ranking.score ?? 0}</p>
                            </div>
                            <div>
                                <span className="text-gray-500">CSAT</span>
                                <p className="text-xl font-bold text-gray-900">
                                    {(ranking.avg_csat ?? jobs.avg_csat_mtd ?? stats.avg_csat_rating ?? 0).toFixed
                                        ? Number(ranking.avg_csat ?? jobs.avg_csat_mtd ?? stats.avg_csat_rating ?? 0).toFixed(1)
                                        : ranking.avg_csat}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500 border-b">
                                    <th className="py-2 px-2 font-medium">#</th>
                                    <th className="py-2 px-2 font-medium">Vendor</th>
                                    <th className="py-2 px-2 font-medium text-right">Score</th>
                                    <th className="py-2 px-2 font-medium text-right hidden sm:table-cell">Completed</th>
                                    <th className="py-2 px-2 font-medium text-right hidden md:table-cell">Rate</th>
                                    <th className="py-2 px-2 font-medium text-right">CSAT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(ranking.leaderboard || []).map((row, idx) => {
                                    const isSelf = row.vendor_id === vendorInfo.id;
                                    return (
                                        <tr
                                            key={row.vendor_id}
                                            className={`border-b border-gray-100 ${isSelf ? 'bg-blue-50' : ''}`}
                                        >
                                            <td className="py-2.5 px-2 text-gray-700">{idx + 1}</td>
                                            <td className="py-2.5 px-2 text-gray-900 font-medium">
                                                {isSelf ? 'You' : row.vendor_name}
                                                {isSelf && ranking.rank > 5 ? (
                                                    <span className="ml-1 text-xs text-blue-600">(#{ranking.rank})</span>
                                                ) : null}
                                            </td>
                                            <td className="py-2.5 px-2 text-right font-semibold text-gray-900">{row.score}</td>
                                            <td className="py-2.5 px-2 text-right text-gray-700 hidden sm:table-cell">{row.completed_mtd}</td>
                                            <td className="py-2.5 px-2 text-right text-gray-700 hidden md:table-cell">{row.completion_rate_mtd}%</td>
                                            <td className="py-2.5 px-2 text-right text-gray-700">{row.avg_csat}</td>
                                        </tr>
                                    );
                                })}
                                {(ranking.leaderboard || []).length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="py-6 text-center text-gray-400">No ranking data yet</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Live ops + feedback */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    <section className="bg-white rounded-lg border border-gray-200 p-4 md:p-5 shadow-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">Driver status</h3>
                        {(data.driver_status || []).length === 0 ? (
                            <p className="text-sm text-gray-400">No drivers</p>
                        ) : (
                            <ul className="space-y-2 max-h-64 overflow-y-auto">
                                {data.driver_status.map((d) => (
                                    <li
                                        key={d.driver?.id}
                                        className="flex items-center justify-between gap-2 text-sm border-b border-gray-50 pb-2"
                                    >
                                        <span className="font-medium text-gray-900 truncate">{d.driver?.name}</span>
                                        <span
                                            className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                                                d.status === 'busy'
                                                    ? 'bg-amber-100 text-amber-800'
                                                    : 'bg-green-100 text-green-800'
                                            }`}
                                        >
                                            {d.status}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <section className="bg-white rounded-lg border border-gray-200 p-4 md:p-5 shadow-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">Recent feedback</h3>
                        {(data.recent_feedback || []).length === 0 ? (
                            <p className="text-sm text-gray-400">No feedback yet</p>
                        ) : (
                            <ul className="space-y-3 max-h-64 overflow-y-auto">
                                {data.recent_feedback.map((f) => (
                                    <li key={f.id} className="text-sm border-b border-gray-50 pb-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-medium text-gray-900 truncate">{f.customer_name}</span>
                                            <span className="inline-flex items-center gap-1 text-amber-600 shrink-0">
                                                <Star className="w-3.5 h-3.5 fill-current" />
                                                {f.overall_rating}
                                            </span>
                                        </div>
                                        {f.feedback_text ? (
                                            <p className="text-gray-600 mt-1 line-clamp-2">{f.feedback_text}</p>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>

                {/* Quick links */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Link to="/services" className="card text-center hover:shadow-md transition-shadow py-4">
                        <p className="font-semibold text-gray-900 text-sm">Services</p>
                    </Link>
                    <Link to="/locations" className="card text-center hover:shadow-md transition-shadow py-4">
                        <p className="font-semibold text-gray-900 text-sm">Locations</p>
                    </Link>
                    <Link to="/service-rates" className="card text-center hover:shadow-md transition-shadow py-4">
                        <p className="font-semibold text-gray-900 text-sm">Service Rates</p>
                    </Link>
                    <Link to="/invoices" className="card text-center hover:shadow-md transition-shadow py-4">
                        <p className="font-semibold text-gray-900 text-sm">Invoices</p>
                    </Link>
                </div>

                {/* Signed-in user */}
                {user && (
                    <p className="text-xs text-gray-400 text-center pb-2">
                        Signed in as {user.name || user.mobile_number} · {user.role || 'Vendor'}
                    </p>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
